import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EqualFilterComponent } from '../equal/equal.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { ReactiveFormsModule } from '@angular/forms';
import { Aggregation, IOption } from '../../filters';
import { updateSelectionCache, writePersistentSelection } from '../filter-groups.helper';

interface ValueParser {
  (option: Aggregation): any;
}
const getKeyAsIs: ValueParser = option => option.key;
const parseNumeric: ValueParser = option => Number.parseInt(option.key);

const valueParserMap: { [key: string]: ValueParser } = {
  id: parseNumeric,
  integer: parseNumeric,
  month: parseNumeric,
  numeric: parseNumeric,
};

@Component({
  selector: 'cbre-multi-select',
  imports: [CommonModule, MultiSelectModule, ReactiveFormsModule],
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
})
export class MultiSelectFilterComponent extends EqualFilterComponent {
  /**
   * Cache of Aggregation objects keyed by parsed value.
   * Preserves displayName for selected values even when absent from current aggregations.
   */
  private _selectedOptionsCache = signal<Map<string | number, Aggregation>>(new Map());

  /**
   * Tracks keys of items that transitioned from 0-count to having matches.
   * These items are displayed at the top of the options list.
   */
  private _recentlyUpdatedKeys = signal<Set<string | number>>(new Set());

  /**
   * Stores the previous aggregation state to detect 0-count → count>0 transitions.
   */
  private _previousAggregations = signal<Aggregation[]>([]);

  protected _setUpFilterOptions() {
    const filter = this.filter$();
    const aggregations = (this._aggregations[filter?.key] ?? []).filter(a => a?.key != null);
    const valueParser = valueParserMap[filter.dataType] ?? getKeyAsIs;

    this._updateSelectedOptionsCache(aggregations, valueParser);
    this._detectRecentlyUpdatedItems(aggregations, valueParser);

    const missingSelected = this._getSelectedOptionsNotInAggregations(aggregations, valueParser);
    const recentlyUpdated = this._recentlyUpdatedKeys();

    const activeOptions: IOption[] = aggregations.map(opt => this._makeLabelValueTransformer(opt, valueParser, false));
    const disabledOptions: IOption[] = missingSelected.map(opt => ({
      label: `${(opt.displayName ?? opt.key ?? '').toUpperCase()} (0)`,
      value: valueParser(opt),
      disabledLabel: true,
    }));

    const recentlyUpdatedOptions: IOption[] = [];
    const regularActiveOptions: IOption[] = [];
    activeOptions.forEach(opt => {
      if (recentlyUpdated.has(opt.value)) {
        recentlyUpdatedOptions.push(opt);
      } else {
        regularActiveOptions.push(opt);
      }
    });

    this.options = [...recentlyUpdatedOptions, ...regularActiveOptions, ...disabledOptions];
    this._previousAggregations.set([...aggregations]);
    this._updateMultiPersistentSelection(valueParser);
    this._checkValuePresence();
  }

  public override handleChange(): void {
    const filter = this.filter$();
    const aggregations = (this._aggregations[filter?.key] ?? []).filter(a => a?.key != null);
    const valueParser = valueParserMap[filter.dataType] ?? getKeyAsIs;
    this._updateSelectedOptionsCache(aggregations, valueParser);
    this._updateMultiPersistentSelection(valueParser);
    super.handleChange();
  }

  private _makeLabelValueTransformer(option: Aggregation, valueParser: ValueParser, isDisabled: boolean): IOption {
    const count = isDisabled ? 0 : option.value == null || isNaN(Number(option.value)) ? null : Number(option.value);
    const base = (option.displayName ?? option.key ?? '').toUpperCase();
    const label = count != null ? `${base} (${count})` : base;
    return { label, value: valueParser(option) };
  }

  private _updateSelectedOptionsCache(currentAggregations: Aggregation[], valueParser: ValueParser): void {
    const selectedValues = this.control?.value ?? [];
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return;

    const cache = new Map(this._selectedOptionsCache());
    updateSelectionCache(cache, selectedValues, currentAggregations, this.filter$(), valueParser);
    this._selectedOptionsCache.set(cache);
  }

  private _getSelectedOptionsNotInAggregations(
    currentAggregations: Aggregation[],
    valueParser: ValueParser
  ): Aggregation[] {
    const selectedValues = this.control?.value ?? [];
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return [];

    const aggregationKeys = new Set(currentAggregations.map(o => valueParser(o)));
    const cache = this._selectedOptionsCache();

    return selectedValues
      .filter((v: string | number) => !aggregationKeys.has(v))
      .map(
        (v: string | number) => cache.get(v) ?? ({ key: String(v), value: 0, displayName: String(v) } as Aggregation)
      );
  }

  private _detectRecentlyUpdatedItems(currentAggregations: Aggregation[], valueParser: ValueParser): void {
    const selectedValues = this.control?.value ?? [];
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
      this._recentlyUpdatedKeys.set(new Set());
      return;
    }

    const previousKeys = new Set(this._previousAggregations().map(o => valueParser(o)));
    const currentKeys = new Set(currentAggregations.map(o => valueParser(o)));
    const updatedKeys = new Set<string | number>();

    selectedValues.forEach((v: string | number) => {
      if (!previousKeys.has(v) && currentKeys.has(v)) {
        updatedKeys.add(v);
      }
    });

    this._recentlyUpdatedKeys.set(updatedKeys);
  }

  private _updateMultiPersistentSelection(_valueParser: ValueParser): void {
    writePersistentSelection(this.filter$(), this.control?.value ?? [], this._selectedOptionsCache());
  }
}
